import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Camera, RotateCcw, ScanLine, History } from "lucide-react";

const READER_ID = "pro-qr-reader";

function ResultCard({ result }) {
  if (!result) return null;
  const styles = {
    valid:    { bg: "bg-emerald-50", bd: "border-emerald-300", tx: "text-emerald-800", icon: <CheckCircle2 size={22} />, title: "Pass VALIDE" },
    inactive: { bg: "bg-amber-50",   bd: "border-amber-300",   tx: "text-amber-800",   icon: <AlertTriangle size={22} />, title: "Pass INACTIF" },
    already_used: { bg: "bg-amber-50", bd: "border-amber-300", tx: "text-amber-800",   icon: <AlertTriangle size={22} />, title: "DÉJÀ UTILISÉ" },
    invalid:  { bg: "bg-rose-50",    bd: "border-rose-300",    tx: "text-rose-800",    icon: <XCircle size={22} />, title: "INVALIDE" },
  }[result.result] || { bg: "bg-slate-50", bd: "border-slate-300", tx: "text-slate-800", icon: <XCircle size={22} />, title: "Résultat" };
  const title = result.title || styles.title;

  return (
    <div className={`mt-4 rounded-2xl border-2 ${styles.bd} ${styles.bg} p-4`} data-testid="scan-result">
      <div className={`flex items-center gap-2 font-heading font-extrabold text-lg ${styles.tx}`}>
        {styles.icon} {title}
      </div>
      {result.reason && (
        <div className={`mt-1 text-sm ${styles.tx}`}>{result.reason}</div>
      )}
      {result.offer && (
        <div className="mt-3 bg-white rounded-xl border border-slate-200 p-3" data-testid="scan-offer">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Offre</div>
          <div className="font-heading font-extrabold text-slate-900">{result.offer.title}</div>
          {result.offer.discount && <div className="text-xs text-slate-500 mt-0.5">{result.offer.discount}</div>}
          {result.customer && (
            <div className="text-xs text-slate-500 mt-1">Client : {result.customer.first_name} {result.customer.last_name}</div>
          )}
        </div>
      )}
      {result.member && (
        <div className="mt-3 bg-white rounded-xl border border-slate-200 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Membre</div>
          <div className="font-heading font-extrabold text-slate-900" data-testid="scan-member-name">
            {result.member.first_name} {result.member.last_name}
          </div>
          {result.member.internal_ref && (
            <div className="text-xs text-slate-500 mt-0.5">Réf : <span className="font-mono">{result.member.internal_ref}</span></div>
          )}
          {result.member.join_date && (
            <div className="text-xs text-slate-500">Adhérent depuis {result.member.join_date}</div>
          )}
        </div>
      )}
      {result.loyalty && (
        <div className="mt-3 bg-emerald-50 rounded-xl border border-emerald-200 p-3" data-testid="scan-loyalty">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Carte de fidélité</div>
          <div className="font-heading font-extrabold text-emerald-900">+1 point · {result.loyalty.points}/{result.loyalty.threshold}</div>
          {result.loyalty.reward_ready && (
            <div className="text-xs text-emerald-800 mt-0.5">🎁 Récompense atteinte{result.loyalty.reward ? ` : ${result.loyalty.reward}` : ""} !</div>
          )}
        </div>
      )}
      {result.organization && (
        <div className="mt-2 bg-white rounded-xl border border-slate-200 p-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Organisme</div>
          <div className="font-heading font-extrabold text-slate-900" data-testid="scan-org-name">
            {result.organization.name}
          </div>
          {result.organization.type_label && (
            <div className="text-xs text-slate-500">{result.organization.type_label}{result.organization.city ? ` · ${result.organization.city}` : ""}</div>
          )}
        </div>
      )}
      <div className="mt-3 text-[11px] text-slate-500">Scanné le {new Date(result.scanned_at).toLocaleString("fr-FR")}</div>
    </div>
  );
}

export default function ProScanner() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [manualToken, setManualToken] = useState("");
  const [scans, setScans] = useState([]);
  const [starting, setStarting] = useState(false);
  const html5QrRef = useRef(null);
  const lastTokenRef = useRef("");
  const lastTimeRef = useRef(0);

  const loadHistory = () => {
    api.get("/pro/pass/scans?limit=15")
      .then((r) => setScans(r.data.items || []))
      .catch(() => {});
  };
  useEffect(() => { loadHistory(); }, []);

  const verify = async (token) => {
    // Anti-double scan (2s)
    const now = Date.now();
    if (token === lastTokenRef.current && now - lastTimeRef.current < 2000) return;
    lastTokenRef.current = token; lastTimeRef.current = now;
    try {
      const isOffer = token.toUpperCase().startsWith("OFR:");
      const r = await api.post(isOffer ? "/pro/offer/verify" : "/pro/pass/verify", { token });
      setResult(r.data);
      if (r.data.result === "valid") {
        toast.success(isOffer ? "Offre validée ✓" : `Pass valide : ${r.data.member?.first_name || ""} ${r.data.member?.last_name || ""}`);
      } else if (r.data.result === "inactive") {
        toast.warning("Membre / organisme inactif");
      } else if (r.data.result === "already_used") {
        toast.warning("Offre déjà utilisée");
      } else {
        toast.error(isOffer ? "Code offre invalide" : "Pass invalide");
      }
      if (!isOffer) loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur de vérification.";
      setResult({ result: "invalid", reason: msg, scanned_at: new Date().toISOString() });
      toast.error(msg);
    }
  };

  const startScan = async () => {
    if (scanning || starting) return;
    setStarting(true);
    setResult(null);
    try {
      // Rendre le conteneur visible AVANT de démarrer (html5-qrcode a besoin
      // des dimensions du conteneur ; sinon la vidéo reste un carré noir).
      await new Promise((r) => setTimeout(r, 200));
      const inst = new Html5Qrcode(READER_ID);
      html5QrRef.current = inst;
      const qrboxFn = (vfW, vfH) => {
        const m = Math.floor(Math.min(vfW, vfH) * 0.7);
        return { width: m, height: m };
      };
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: qrboxFn, aspectRatio: 1.0 },
        (decoded) => { verify(decoded); },
        () => {} // ignore parse errors
      );
      setScanning(true);
    } catch (e) {
      toast.error("Impossible d'accéder à la caméra. Autorisez l'accès à la caméra dans votre navigateur, puis réessayez.");
      try { if (html5QrRef.current) { await html5QrRef.current.clear(); } } catch (err) { console.debug("qr clear failed", err); }
      html5QrRef.current = null;
    } finally {
      setStarting(false);
    }
  };

  const stopScan = async () => {
    try {
      if (html5QrRef.current) {
        await html5QrRef.current.stop();
        await html5QrRef.current.clear();
      }
    } catch (err) { console.debug("qr stop failed", err); }
    html5QrRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopScan(); }, []);

  const submitManual = (e) => {
    e.preventDefault();
    if (!manualToken.trim()) return;
    verify(manualToken.trim());
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="pro-scanner-page">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2">
          <ScanLine size={22} className="text-[#E05638]" /> Scanner de pass membre
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Scannez le <b>Pass membre</b> d'un organisme, ou le <b>code-barres d'une offre</b> présenté par un client, pour le valider.
        </p>
      </header>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Camera size={16} /> Caméra {scanning ? "active" : "inactive"}
          </div>
          {!scanning ? (
            <Button
              onClick={startScan}
              disabled={starting}
              className="bg-[#E05638] hover:bg-[#c74a2f]"
              data-testid="scanner-start"
            >
              <Camera size={16} className="mr-2" /> {starting ? "Démarrage…" : "Démarrer la caméra"}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={stopScan}
              data-testid="scanner-stop"
            >
              <RotateCcw size={16} className="mr-2" /> Arrêter
            </Button>
          )}
        </div>

        <div className={`mt-3 rounded-xl overflow-hidden bg-slate-900 aspect-square max-w-md mx-auto ${(scanning || starting) ? "" : "hidden"}`}>
          <div id={READER_ID} style={{ width: "100%" }} />
        </div>
        {!scanning && !starting && (
          <div className="mt-3 aspect-square max-w-md mx-auto grid place-items-center rounded-xl border-2 border-dashed border-slate-200 text-slate-400 text-sm">
            <div className="text-center">
              <Camera size={40} className="mx-auto mb-2 opacity-60" />
              Cliquez sur « Démarrer la caméra » pour scanner un pass.
            </div>
          </div>
        )}

        <form onSubmit={submitManual} className="mt-4 flex gap-2">
          <Input
            placeholder="Ou collez ici le token du pass (mode manuel)"
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            data-testid="scanner-manual-input"
          />
          <Button type="submit" variant="outline" data-testid="scanner-manual-submit">Vérifier</Button>
        </form>

        <ResultCard result={result} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="font-heading font-extrabold text-slate-900 flex items-center gap-2">
          <History size={16} /> Derniers scans
        </div>
        {scans.length === 0 ? (
          <div className="mt-2 text-sm text-slate-500">Aucun scan pour le moment.</div>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100" data-testid="scan-history">
            {scans.map((s) => (
              <li key={s.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{s.member_name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {s.organization_name} · {new Date(s.scanned_at).toLocaleString("fr-FR")}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                    s.result === "valid"
                      ? "bg-emerald-100 text-emerald-800"
                      : s.result === "inactive"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-rose-100 text-rose-800"
                  }`}
                >{s.result}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
